import React, { Suspense, useCallback, useMemo } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, LinearProgress, Stack, Typography } from '@lyearn/ui';
import Loader from '@/components/Loader';
import AttemptHistory from '@/modules/corporate/modules/quiz/views/QuizDetails/components/AttemptHistory';
import CountdownTimer from '@/modules/corporate/modules/quiz/views/QuizDetails/components/QuizDetailsQuizPlayer/components/QuizPlayerHeader/components/QuestionHeader/components/CountdownTimer';
import useRetakeQuiz from '@/modules/corporate/modules/quiz/views/QuizDetails/hooks/useRetakeQuiz';
import { getCanRetakeQuiz } from '@/modules/corporate/modules/quiz/views/QuizDetails/utils/getCanRetakeQuiz';
import QuizPlayer from '@/modules/corporate/modules/quiz/views/QuizPlayer';
import { ViewTypeEnum } from '@/modules/corporate/modules/quiz/views/QuizPlayer/hooks/useQuizPlayer/types';
import {
  RenderQuizPlayerHeaderParams,
  RenderQuizPlayerQuestionActionParams,
} from '@/modules/corporate/modules/quiz/views/QuizPlayer/types';
import { TimeLimitApplicableOnEnum } from '@/types/schema';

import { QuizPlayerContainerProps } from '../../types';
import PopupQuizCard from '../PopupQuizContainer/components/PopupQuizCard';
import { quizCardMobileStyles, quizCardStyles } from '../PopupQuizContainer/styles';
import InlineQuizNavigationActions from './components/InlineQuizNavigationActions';
import QuizHistoryButton from './components/QuizHistoryButton';
import { getShowAttemptHistory } from './utils';

function InlineQuizContainer(props: QuizPlayerContainerProps) {
  const { courseId, userId, quiz, quizPerformance, onQuizSubmit, isLocalizedContent } = props;
  const { t } = useTranslation();
  const { isMobile } = useScreen();
  const [isPlaying, { on: startPlaying, off: stopPlaying }] = useToggle(false);
  const [showAttemptHistory, { toggle: toggleAttemptHistory }] = useToggle(false);

  const { executeRetakeQuiz } = useRetakeQuiz({
    parentEntityId: courseId,
    quizId: quiz.id,
    isEmbed: true,
  });

  const handleStartOrRetake = useCallback(async () => {
    // A finished attempt that still allows retakes needs a fresh attempt created
    // before the player mounts; otherwise startQuiz resumes the submitted attempt
    // and the player opens straight into Review.
    const { canRetakeQuiz } = getCanRetakeQuiz(quiz, quizPerformance);
    if (canRetakeQuiz) {
      await executeRetakeQuiz();
    }
    startPlaying();
  }, [executeRetakeQuiz, quiz, quizPerformance, startPlaying]);

  const handleQuizSubmit = useCallback(
    (params: Parameters<NonNullable<QuizPlayerContainerProps['onQuizSubmit']>>[0]) => {
      onQuizSubmit?.(params);
      stopPlaying();
    },
    [onQuizSubmit, stopPlaying],
  );

  const renderHeader = useCallback(
    ({ content, view, timeLeft }: RenderQuizPlayerHeaderParams) => {
      if (view.type !== ViewTypeEnum.Question) {
        return null;
      }

      // Quiz-level time limit shows a countdown here; per-question time limit is
      // rendered as a Timerbar inside the question by the player Body.
      const isQuizTimed =
        content.settings?.timeLimitApplicableOn === TimeLimitApplicableOnEnum.Quiz;

      return (
        <Stack alignItems="center" className="relative px-20 pt-20 pb-4" justifyContent="between">
          <LinearProgress
            className="absolute inset-x-0 top-0"
            size="x-small"
            value={((view.index + 1) / content.quizQuestionsConfig.questionCount) * 100}
          />
          <Typography className="text-text-secondary" variant="heading-xxs">
            {t('QuizPlayer:QuestionProgress', {
              current: view.index + 1,
              total: content.quizQuestionsConfig.questionCount,
            })}
          </Typography>
          {isQuizTimed ? <CountdownTimer timeLeft={timeLeft} /> : null}
        </Stack>
      );
    },
    [t],
  );

  const renderQuestionNavigation = useCallback(
    (params: RenderQuizPlayerQuestionActionParams) => (
      <InlineQuizNavigationActions {...params} onCancel={stopPlaying} />
    ),
    [stopPlaying],
  );

  const quizPlayerConfig = useMemo(
    () => ({
      animateQuestion: true,
      resumeQuiz: true,
      hideSidebar: true,
      autoAdvanceOnSelect: true,
      hideMobileSubmitButton: true,
      // renderHeader below already shows a "Question X of Y" progress label.
      hideQuestionProgressLabel: true,
    }),
    [],
  );

  const quizPlayerRenderers = useMemo(
    () => ({ renderHeader, renderQuestionNavigation }),
    [renderHeader, renderQuestionNavigation],
  );

  // Memoized so PopupQuizCard's inner memo() isn't defeated by a fresh element
  // on every render.
  const headerAction = useMemo(
    () =>
      getShowAttemptHistory(quizPerformance) ? (
        <QuizHistoryButton onClick={toggleAttemptHistory} />
      ) : null,
    [quizPerformance, toggleAttemptHistory],
  );

  if (isPlaying) {
    return (
      <Box
        className={cn(
          'flex flex-col bg-bg-primary border border-border rounded-16 overflow-hidden min-h-[32rem]',
          isMobile ? quizCardMobileStyles : quizCardStyles,
        )}>
        <Suspense
          fallback={
            <Box className="flex flex-1 items-center justify-center">
              <Loader />
            </Box>
          }>
          <Box className="flex flex-1 flex-col">
            <QuizPlayer
              isEmbed
              classes={{ player: 'w-full', question: 'px-20 pb-20' }}
              config={quizPlayerConfig}
              isLocalizedContent={isLocalizedContent}
              parentEntityId={courseId}
              quizId={quiz.id}
              renderers={quizPlayerRenderers}
              userId={userId}
              onQuizSubmit={handleQuizSubmit}
            />
          </Box>
        </Suspense>
      </Box>
    );
  }

  return (
    <>
      <PopupQuizCard
        className={cn('cursor-pointer', isMobile ? quizCardMobileStyles : quizCardStyles)}
        headerAction={headerAction}
        quiz={quiz}
        quizPerformance={quizPerformance}
        onClick={handleStartOrRetake}
      />
      {/* Reviewing goes through AttemptHistory -> QuizPlayerReview, never through
          QuizPlayer, so opening it can't create or consume an attempt. */}
      {showAttemptHistory ? (
        <AttemptHistory
          isEmbed
          content={quiz}
          isLocalizedContent={isLocalizedContent}
          learnerId={userId}
          parentEntityId={courseId}
          quizPerformance={quizPerformance}
          onClose={toggleAttemptHistory}
        />
      ) : null}
    </>
  );
}

export default InlineQuizContainer;
